import { useEffect, useRef } from 'react';

interface UseIntersectionObserverOptions {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  onIntersect: () => void;
  enabled?: boolean;
  minVisibleMs?: number; // Require visibility for at least this many ms before firing
  triggerOnce?: boolean; // Fire only once and then disconnect
}

export const useIntersectionObserver = (
  options: UseIntersectionObserverOptions,
) => {
  const targetRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timerRef = useRef<number | null>(null);
  const hasFiredRef = useRef(false);

  const {
    root = null,
    rootMargin = '0px',
    threshold = 0.1,
    onIntersect,
    enabled = true,
    minVisibleMs = 0,
    triggerOnce = false,
  } = options;

  useEffect(() => {
    if (!enabled || !targetRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasFiredRef.current) {
          if (minVisibleMs > 0) {
            // Start debounce timer
            if (timerRef.current == null) {
              timerRef.current = window.setTimeout(() => {
                timerRef.current = null;
                if (hasFiredRef.current) return;
                onIntersect();
                if (triggerOnce) {
                  hasFiredRef.current = true;
                  observerRef.current?.disconnect();
                }
              }, minVisibleMs);
            }
          } else {
            onIntersect();
            if (triggerOnce) {
              hasFiredRef.current = true;
              observerRef.current?.disconnect();
            }
          }
        } else {
          // Not intersecting: cancel pending timer
          if (timerRef.current != null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
          }
        }
      },
      {
        root,
        rootMargin,
        threshold,
      },
    );

    observerRef.current.observe(targetRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      hasFiredRef.current = false;
    };
  }, [
    root,
    rootMargin,
    threshold,
    onIntersect,
    enabled,
    minVisibleMs,
    triggerOnce,
  ]);

  return targetRef;
};
